import { Link } from "react-router";
import { Card, EmptyState, KanbanCard, Stack, Cluster, Tooltip } from "@salesapp/ui";
import { useAuth } from "../shell/AuthProvider";
import { useAccess } from "../shell/AccessProvider";
import { appUrl } from "../lib/customerUrl";
import {
  crmApi,
  type OrgDashboard,
  type PipelinePreviewColumn,
  type QuotaSnapshot,
  type SparklineSeries,
} from "../lib/crmApi";
import { useEffect, useState } from "react";
import "./pages.css";
import { useLocale } from "../shell/LocaleProvider";

export function HomePage() {
  const { session } = useAuth();
  const { can, status } = useAccess();
  const { formatMoney, formatDate, orgCurrency } = useLocale();
  const money = (value: number | string | null | undefined) => formatMoney(value, orgCurrency, true);
  const [dashboard, setDashboard] = useState<OrgDashboard | null>(null);

  useEffect(() => {
    if (!session?.orgId || !can("crm.dashboard.view")) return;
    void crmApi.getOrgDashboard(session.orgId).then(setDashboard).catch(() => setDashboard(null));
  }, [session?.orgId, can]);

  const kpi = dashboard?.kpi;
  const accessLoading = status === "loading";
  const crmReady = can("crm.companies");
  const canLeads = can("crm.leads");
  const canPipeline = can("crm.opportunities");
  const canForecast = can("crm.forecast");
  const crmHref = (path: string) => appUrl("crm", session?.orgSlug, path);
  const rangeText = (start?: string, end?: string) => {
    if (!start || !end) return "";
    return `${formatDate(start)} – ${formatDate(end)}`;
  };

  return (
    <Stack gap={6}>
      <div>
        <h1 className="home-title">My Stats</h1>
        <p className="home-subtitle">
          Your quick view of items you control — open leads, follow-ups, pipeline, and quota.
        </p>
      </div>

      <div className={canForecast ? "home-hero-grid" : undefined}>
        <Card data-tour="get-started">
          <Tooltip
            label="About Get started"
            text="Your fastest next action. Capture a new lead when someone raises their hand, or open Companies to continue an existing account through the sales cycle."
          >
            <h2 className="home-section-title">Get started</h2>
          </Tooltip>
          {accessLoading ? (
            <p className="home-subtitle">Loading your workspace…</p>
          ) : crmReady ? (
            <Cluster gap={3}>
              <Link to={crmHref("/leads")} className="home-action" data-tour="capture-lead">Capture a lead</Link>
              <Link to={crmHref("/companies")} className="home-action home-action--secondary" data-tour="open-companies">Open companies</Link>
            </Cluster>
          ) : (
            <EmptyState title="CRM is not available" description="Ask an organization admin to assign a CRM seat." />
          )}
        </Card>
        {canForecast && dashboard?.sparklines?.quarter && (
          <SparklineCard
            title="This quarter"
            series={dashboard.sparklines.quarter}
            href={crmHref("/forecast")}
            money={money}
            tooltip={`Closed-won value for ${dashboard.sparklines.quarter.label} (${rangeText(dashboard.sparklines.quarter.start, dashboard.sparklines.quarter.end)}). Open Forecast to see the full target.`}
          />
        )}
        {canForecast && dashboard?.sparklines?.ytd && (
          <SparklineCard
            title="Year to date"
            series={dashboard.sparklines.ytd}
            href={crmHref("/forecast")}
            money={money}
            tooltip={`${dashboard.sparklines.ytd.label} (${rangeText(dashboard.sparklines.ytd.start, dashboard.sparklines.ytd.end)}). This is closed-won value from 1 January through today.`}
          />
        )}
      </div>

      <div className="home-kpi-grid">
        <KpiCard
          label="Open leads"
          value={kpi ? String(kpi.open_leads) : "—"}
          delta={accessLoading ? "Loading" : crmReady ? "From your CRM" : "CRM not entitled"}
          tooltip="Leads that are still active — not converted to an account and not disqualified. A healthy desk keeps this moving: new names come in, get worked, and either convert or get closed out. Sitting inventory usually means follow-up has stalled."
          href={canLeads ? crmHref("/leads?open=1") : undefined}
        />
        <KpiCard
          label="Hot leads"
          value={kpi ? String(kpi.hot_leads) : "—"}
          delta="Temperature = hot"
          tooltip="Active leads marked hot. These are the highest-intent names and should have a next action booked. Ideal state: every hot lead has an owner and a date on the calendar within a day or two."
          href={canLeads ? crmHref("/leads?rating=hot&open=1") : undefined}
        />
        <KpiCard
          label="Follow-ups today"
          value={kpi ? String(kpi.follow_ups_today) : "—"}
          delta={kpi ? `${kpi.follow_ups_leads ?? 0} leads · ${kpi.follow_ups_cycle ?? 0} cycle tasks` : "Due today"}
          tooltip="Work due today from two places: a lead's next-action date, plus open sales-cycle tasks whose due date is today (the stage SLAs under Sales cycle → Settings). Ideal state is zero by end of day — anything left over is already slipping."
          href={crmReady ? crmHref("/follow-ups") : undefined}
        />
        {canPipeline ? (
          <KpiCard
            label="Open pipeline"
            value={kpi?.open_pipeline_value != null ? money(kpi.open_pipeline_value) : "—"}
            delta="Unweighted open deals"
            tooltip="Sum of deal value on opportunities that are still open — not won or lost. This is unweighted, so it is the full ask, not the probability-adjusted forecast. A strong book has enough open value to cover quota even after some deals slip."
            href={crmHref("/opportunities?open=1")}
          />
        ) : (
          <KpiCard
            label="Activities today"
            value={kpi ? String(kpi.activities_today) : "—"}
            delta="Logged today"
            tooltip="Activities logged today. A working day should show conversations, not a blank page."
          />
        )}
        {canPipeline && (
          <KpiCard
            label="Won this year"
            value={kpi ? money(kpi.won_this_year ?? 0) : "—"}
            delta="Closed-won value"
            tooltip="Revenue from opportunities marked closed-won this calendar year. This is the number that feeds quota attainment. Ideal state: won value is on or ahead of the year-to-date slice of your assigned target."
            href={canForecast ? crmHref("/forecast") : crmHref("/opportunities")}
          />
        )}
      </div>

      {(canPipeline || canForecast) && (
        <div className="home-split">
          {canPipeline && (
            <Card>
              <Tooltip
                label="About pipeline preview"
                text="A compact view of live deals by stage: New (lead in), Qualified, Proposal (including negotiation), and Won. Ideal state: deals keep moving right. A fat New column with an empty Proposal column means qualification is stalling."
              >
                <h2 className="home-section-title">
                  <Link to={crmHref("/opportunities")} className="home-section-link">Pipeline preview</Link>
                </h2>
              </Tooltip>
              <div className="home-pipeline-board">
                {(dashboard?.pipeline_preview ?? FALLBACK_PIPELINE).map((column) => (
                  <PipelineColumn key={column.key} column={column} hrefFor={(id) => crmHref(`/opportunities/${id}`)} />
                ))}
              </div>
            </Card>
          )}
          {canForecast && (
            <Card>
              <Tooltip
                label="About quota attainment"
                text={
                  dashboard?.quota
                    ? `The needle is closed-won value for ${dashboard.quota.period_label} (${rangeText(dashboard.quota.period_start, dashboard.quota.period_end)}) as a percentage of the sales target for that period. Year to date runs ${rangeText(dashboard.quota.year_start, dashboard.quota.year_end)}. Set targets under Forecast & KPI.`
                    : "The needle is closed-won value for the current period as a percentage of the sales target for that period. Set targets under Forecast & KPI."
                }
              >
                <h2 className="home-section-title">
                  <Link to={crmHref("/forecast")} className="home-section-link">Quota attainment</Link>
                </h2>
              </Tooltip>
              <QuotaGauge quota={dashboard?.quota} href={crmHref("/forecast")} />
            </Card>
          )}
        </div>
      )}
    </Stack>
  );
}

const FALLBACK_PIPELINE: PipelinePreviewColumn[] = [
  { key: "new", label: "New", count: 0, value: 0, deals: [] },
  { key: "qualified", label: "Qualified", count: 0, value: 0, deals: [] },
  { key: "proposal", label: "Proposal", count: 0, value: 0, deals: [] },
  { key: "won", label: "Won", count: 0, value: 0, deals: [] },
];

function PipelineColumn({ column, hrefFor }: { column: PipelinePreviewColumn; hrefFor: (id: string) => string }) {
  const { formatMoney, orgCurrency } = useLocale();
  const money = (value: number | string | null | undefined, currency?: string | null) =>
    formatMoney(value, currency || orgCurrency, true);
  return (
    <div className="home-pipeline-column">
      <div className="home-pipeline-column-head">
        <span>{column.label}</span>
        <span>{column.count}</span>
      </div>
      <div className="home-pipeline-column-body">
        {column.deals.length === 0 ? (
          <p className="home-pipeline-empty">No records in this stage</p>
        ) : (
          column.deals.map((deal) => (
            <Link key={deal.id} to={hrefFor(deal.id)} className="home-pipeline-card-link">
              <KanbanCard title={deal.title} subtitle={deal.company_name ?? undefined} value={money(deal.value, deal.currency)} />
            </Link>
          ))
        )}
      </div>
    </div>
  );
}

const GAUGE_CX = 120;
const GAUGE_CY = 128;
const GAUGE_RADIUS = 90;
const GAUGE_NEEDLE = 68;
const GAUGE_ZONES = [
  { start: 180, end: 135, className: "home-quota-zone--cold" },
  { start: 135, end: 90, className: "home-quota-zone--behind" },
  { start: 90, end: 36, className: "home-quota-zone--pace" },
  { start: 36, end: 0, className: "home-quota-zone--hit" },
] as const;

function QuotaGauge({ quota, href }: { quota?: QuotaSnapshot; href: string }) {
  const { formatMoney, orgCurrency } = useLocale();
  const money = (value: number | string | null | undefined) => formatMoney(value, orgCurrency, true);
  const pct = Math.max(0, quota?.pct ?? 0);
  const needle = Math.min(100, pct);
  const angle = 180 - needle * 1.8;
  const tone = pct >= 100 ? "is-hit" : pct >= 80 ? "is-on-pace" : pct >= 50 ? "is-behind" : "is-cold";
  const subject =
    quota?.subject === "personal"
      ? "Your quarterly target"
      : quota?.subject === "team"
        ? "Team quarterly target"
        : "No target assigned yet";
  const tip = polar(GAUGE_CX, GAUGE_CY, GAUGE_NEEDLE, angle);
  return (
    <Link to={href} className={`home-quota ${tone} home-quota-link`}>
      <svg viewBox="0 0 240 152" className="home-quota-svg" role="img" aria-label={`Quota ${needle}% attained`}>
        <path d={arc(GAUGE_CX, GAUGE_CY, GAUGE_RADIUS, 180, 0)} className="home-quota-track" strokeWidth={34} />
        {GAUGE_ZONES.map((zone) => (
          <path
            key={zone.className}
            d={arc(GAUGE_CX, GAUGE_CY, GAUGE_RADIUS, zone.start - 1.6, zone.end + 1.6)}
            className={`home-quota-zone ${zone.className}`}
            strokeWidth={30}
          />
        ))}
        <line
          x1={GAUGE_CX}
          y1={GAUGE_CY}
          x2={tip[0]}
          y2={tip[1]}
          className="home-quota-needle"
          strokeWidth={5}
        />
        <circle cx={GAUGE_CX} cy={GAUGE_CY} r="8" className="home-quota-hub" />
        <text x="22" y="148" className="home-quota-caption">0</text>
        <text x="218" y="148" className="home-quota-caption" textAnchor="end">Attained</text>
      </svg>
      <div className="home-quota-readout">
        <div className="home-kpi-value">{pct.toFixed(0)}%</div>
        <p className="home-subtitle">
          {money(quota?.attained ?? 0)} of {money(quota?.target ?? 0)} · {quota?.period_label ?? "This quarter"}
        </p>
        <p className="home-subtitle">
          {subject}. Year {quota?.year_label ?? ""}: {money(quota?.ytd_attained ?? 0)} of {money(quota?.yearly_target ?? 0)}.
        </p>
      </div>
    </Link>
  );
}

function SparklineCard({
  title,
  series,
  href,
  money,
  tooltip,
}: {
  title: string;
  series: SparklineSeries;
  href: string;
  money: (value: number | string | null | undefined) => string;
  tooltip: string;
}) {
  return (
    <Card>
      {/* The tooltip trigger sits outside the link so opening the hint does not navigate away. */}
      <Tooltip text={tooltip} label={`About ${title}`}>
        <h2 className="home-section-title">{title}</h2>
      </Tooltip>
      <Link to={href} className="home-sparkline-link">
        <div className="home-kpi-value">{money(series.total)}</div>
        <p className="home-subtitle">{series.label}</p>
        <Sparkline series={series} caption={tooltip} />
      </Link>
    </Card>
  );
}

function Sparkline({ series, caption }: { series: SparklineSeries; caption: string }) {
  const points = series.points.length ? series.points : [{ label: "—", value: 0 }];
  const max = Math.max(...points.map((point) => point.value), 1);
  const min = Math.min(...points.map((point) => point.value), 0);
  const range = max - min || 1;
  const stepX = points.length > 1 ? 100 / (points.length - 1) : 0;
  const plotted = points
    .map((point, index) => `${index * stepX},${28 - ((point.value - min) / range) * 24}`)
    .join(" ");
  return (
    <svg viewBox="0 0 100 32" className="home-sparkline" role="img" aria-label={caption}>
      <title>{caption}</title>
      <polyline points={plotted} className="home-sparkline-line" />
    </svg>
  );
}

function arc(cx: number, cy: number, r: number, startDeg: number, endDeg: number): string {
  const start = polar(cx, cy, r, startDeg);
  const end = polar(cx, cy, r, endDeg);
  const large = startDeg - endDeg > 180 ? 1 : 0;
  return `M ${start[0]} ${start[1]} A ${r} ${r} 0 ${large} 1 ${end[0]} ${end[1]}`;
}

function polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const rad = (deg * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy - r * Math.sin(rad)];
}

function KpiCard({
  label,
  value,
  delta,
  tooltip,
  href,
}: {
  label: string;
  value: string;
  delta: string;
  tooltip: string;
  href?: string;
}) {
  const body = (
    <>
      <Tooltip text={tooltip} label={`About ${label}`}>
        <div className="home-kpi-label">{label}</div>
      </Tooltip>
      <div className="home-kpi-value">{value}</div>
      <div className="home-kpi-delta is-neutral">{delta}</div>
    </>
  );
  return (
    <Card>
      {href ? <Link to={href} className="home-kpi-link">{body}</Link> : body}
    </Card>
  );
}
